/**
 * Microphone Permission Utilities
 * Handles checking and requesting microphone permissions
 */

import logger from './logger';

export type MicrophonePermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

/**
 * Check current microphone permission status
 */
export async function checkMicrophonePermission(): Promise<MicrophonePermissionState> {
  try {
    // Check if Permissions API is supported
    if (!navigator.permissions) {
      logger.log('[MicrophonePermission] Permissions API not supported, will prompt on access');
      return 'unsupported';
    }

    // Query microphone permission
    const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    logger.log('[MicrophonePermission] Current state:', result.state);
    return result.state as MicrophonePermissionState;
  } catch (error) {
    console.warn('[MicrophonePermission] Failed to query permission:', error);
    return 'unsupported';
  }
}

/**
 * Request microphone permission by attempting to access it
 * Returns true if granted, false if denied
 */
export async function requestMicrophonePermission(): Promise<boolean> {
  try {
    logger.log('[MicrophonePermission] Requesting microphone access...');
    
    const stream = await navigator.mediaDevices.getUserMedia({ 
      audio: true 
    });
    
    // Permission granted - immediately stop the stream
    logger.log('[MicrophonePermission] Permission granted');
    stream.getTracks().forEach(track => track.stop());
    return true;
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        logger.log('[MicrophonePermission] Permission denied by user');
      } else if (error.name === 'NotFoundError') {
        console.error('[MicrophonePermission] No microphone found');
      } else {
        console.error('[MicrophonePermission] Error requesting permission:', error);
      }
    }
    return false;
  }
}

/**
 * Check if microphone permission is currently granted
 */
export async function isMicrophonePermissionGranted(): Promise<boolean> {
  const state = await checkMicrophonePermission();
  return state === 'granted';
}

/**
 * Get user-friendly message for permission state
 */
export function getMicrophonePermissionMessage(state: MicrophonePermissionState): string {
  switch (state) {
    case 'granted':
      return 'Microphone access granted';
    case 'denied':
      return 'Microphone access denied. Please enable it in your browser settings.';
    case 'prompt':
      return 'Click to allow microphone access';
    case 'unsupported':
      return 'Click to request microphone access';
    default:
      return 'Unknown permission state';
  }
}

/**
 * Listen for permission state changes
 */
export function onMicrophonePermissionChange(
  callback: (state: MicrophonePermissionState) => void
): () => void {
  let permissionStatus: PermissionStatus | null = null;

  const setup = async () => {
    try {
      if (!navigator.permissions) return;
      
      permissionStatus = await navigator.permissions.query({ 
        name: 'microphone' as PermissionName 
      });
      
      const handleChange = () => {
        callback(permissionStatus!.state as MicrophonePermissionState);
      };
      
      permissionStatus.addEventListener('change', handleChange);
      
      // Call immediately with current state
      callback(permissionStatus.state as MicrophonePermissionState);
    } catch (error) {
      console.warn('[MicrophonePermission] Cannot listen to permission changes:', error);
    }
  };

  setup();

  // Return cleanup function
  return () => {
    if (permissionStatus) {
      permissionStatus.removeEventListener('change', () => {});
    }
  };
}
